import { responsiveImageProps } from '../lib/publicMedia';
import { usePublicPageData } from './public/PublicPageData';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import usePublicFooter from './public/usePublicFooter';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';


const DEFAULT_FOOTER = {
  operated_by: 'Just Vitality Clinic',
  address: '',
  phone: '',
  email: '',
  hours: '',
  column1_heading: 'Services',
  column1_links: [
    { label: 'IV Therapy', href: '/iv-supplements-hull' },
    { label: 'Rehabilitation & Physiotherapy', href: '/physiotherapy' },
    { label: 'Blood Tests', href: '/blood-test' },
    { label: 'Training Courses', href: '/training-hull' },
    { label: 'Blog', href: '/blog' },
    { label: 'Book Online', href: '/booking' },
  ],
  column2_heading: 'Legal',
  column2_links: [
    { label: 'Terms & Conditions', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
  ],
  social_links: [],
  company_name: 'Just Vitality Clinic',
  company_number: '16805119',
  company_url: '',
};

const FooterLink = ({ item, className }) => {
  const href = item?.href || '#';
  const label = item?.label || href;
  if (/^https?:\/\//i.test(href)) return <a href={href} target="_blank" rel="noreferrer" className={className}>{label}</a>;
  return <Link to={href} className={className}>{label}</Link>;
};

const PublicFooter = () => {
  const initial = usePublicPageData();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { footer: publishedFooter, error } = usePublicFooter(initial?.footer);
  const footer = { ...DEFAULT_FOOTER, ...publishedFooter };

  const hover = 'hover:text-[#C9A96E] transition-colors';
  return <footer data-testid="site-footer" className={`border-t ${isDark ? 'bg-[#0a0704] border-[#C9A96E]/20' : 'bg-slate-100 border-slate-200'}`}>
    {error && <p role="status" className="px-4 py-3">{error} <Link to="/contact">Contact</Link></p>}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12"><div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
      <div><Link to="/" onDoubleClick={e => { e.preventDefault(); navigate('/login'); }} className="flex items-center gap-3 mb-5"><img src="/images/logo.jpeg" width="40" height="40" {...responsiveImageProps('/images/logo.jpeg', '40px')} alt="Just Vitality Clinic" loading="lazy" className="w-10 h-10 rounded-xl object-cover" onError={e => { e.target.style.display = 'none'; }} /><span className="text-lg font-semibold"><span className="text-[#C9A96E]">Just Vitality</span> Clinic</span></Link><div className={`space-y-3 text-sm ${isDark ? 'text-[#C4A882]' : 'text-slate-500'}`}>{footer.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#C9A96E] flex-shrink-0" /><span>{footer.address}</span></div>}{footer.phone && <a href={`tel:${String(footer.phone).replace(/[^\d+]/g, '')}`} className={`flex items-center gap-2 ${hover}`}><Phone className="w-4 h-4 text-[#C9A96E] flex-shrink-0" /><span>{footer.phone}</span></a>}{footer.email && <a href={`mailto:${footer.email}`} className={`flex items-center gap-2 ${hover}`}><Mail className="w-4 h-4 text-[#C9A96E] flex-shrink-0" /><span>{footer.email}</span></a>}{footer.hours && <div>{footer.hours}</div>}</div></div>
      <div><h4 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>{footer.column1_heading || 'Services'}</h4><nav className={`flex flex-col gap-2.5 text-sm ${isDark ? 'text-[#C4A882]' : 'text-slate-500'}`}>{(footer.column1_links || []).map((item, index) => <FooterLink key={`${item.href}-${index}`} item={item} className={hover} />)}</nav></div>
      <div><h4 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>{footer.column2_heading || 'Legal'}</h4><nav className={`flex flex-col gap-2.5 text-sm ${isDark ? 'text-[#C4A882]' : 'text-slate-500'}`}>{(footer.column2_links || []).map((item, index) => <FooterLink key={`${item.href}-${index}`} item={item} className={hover} />)}</nav>{(footer.social_links || []).length > 0 && <nav className={`flex flex-wrap gap-x-4 gap-y-2 mt-5 text-sm ${isDark ? 'text-[#C4A882]' : 'text-slate-500'}`}>{footer.social_links.map((item, index) => <FooterLink key={`${item.href}-${index}`} item={item} className={hover} />)}</nav>}</div>
    </div></div>
    <div className={`border-t ${isDark ? 'border-[#C9A96E]/10' : 'border-slate-200'}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col items-center gap-2 text-xs sm:grid sm:grid-cols-[1fr_auto_1fr] ${isDark ? 'text-[#9a8a7a]' : 'text-slate-400'}`}>
        <span className="sm:justify-self-start">&copy; {new Date().getFullYear()} {footer.company_name || 'Just Vitality Clinic'}. All rights reserved.</span>
        <span className="sm:justify-self-center">POWERED BY{' '}<a href="https://nexops.consulting/" target="_blank" rel="noopener noreferrer" className="font-bold text-white hover:text-[#C9A96E] transition-colors">NexOps</a></span>
        {footer.company_number && (footer.company_url ? <a href={footer.company_url} target="_blank" rel="noreferrer" className={`${hover} sm:justify-self-end`}>Company Registration No. {footer.company_number}</a> : <span className="sm:justify-self-end">Company Registration No. {footer.company_number}</span>)}
      </div>
    </div>
  </footer>;
};

export default PublicFooter;
